import { InputHTMLAttributes, ReactNode, forwardRef, useEffect } from 'react';
import { ErrorMessage, Field, FieldProps, useField } from 'formik';
import { useCalendarFullStore } from '@/service/state/FullCalendar/useFullCalendarStore';
import { cn } from '@/service/utils/cn';

type TFormInputProps = InputHTMLAttributes<HTMLInputElement> & {
    name: string;
    label?: string;
    errorIcon?: ReactNode;
    required?: boolean;
    showErrorMessage?: boolean;
    className?: string;
    fieldClassName?: string;
    errorClassName?: string;
    fieldAs?:
        | React.ComponentType<FieldProps<unknown>['field']>
        | string
        | React.ComponentType
        | React.ForwardRefExoticComponent<unknown>;
};

export const FormInput = forwardRef<HTMLInputElement, TFormInputProps>(function FormInputRef(
    {
        className,
        errorClassName,
        fieldClassName,
        name,
        label,
        errorIcon,
        required,
        showErrorMessage = true,
        fieldAs,
        disabled,
        ...inputProps
    },
    ref
) {
    const [{ value }] = useField(name);

    const { setTripSettingsAreFilled } = useCalendarFullStore();

    useEffect(() => {
        setTripSettingsAreFilled(!!value);
    }, [setTripSettingsAreFilled, value]);

    return (
        <div
            className={cn('group relative flex w-full flex-col gap-[7px] lg:gap-[11px]', className)}
        >
            <label
                htmlFor={name}
                className={`tx-large-semibold max-w-max text-gray-6 group-[.promo-code-widget]:tx-large-medium dark:text-white ${
                    required ? "after:ml-1 after:text-state-error after:content-['*']" : ''
                }`}
            >
                {label}
            </label>
            <Field
                ref={ref}
                as={fieldAs}
                className={cn(
                    'tx-large-medium h-10 w-full rounded-[50px] border border-gray-3 py-0 pl-[30px] pr-[25px] text-base text-gray-1 outline-none group-[.promo-code-widget]:h-9 group-[.promo-code-widget]:pl-5 dark:border-dark-1 dark:bg-dark-3 dark:text-gray-3 md:group-[.promo-code-widget]:h-[50px] md:group-[.promo-code-widget]:pl-[30px] lg:h-[50px] lg:group-[.promo-code-widget]:h-[45px]',
                    fieldClassName,
                    disabled && 'cursor-not-allowed bg-gray-4 text-gray-2 dark:bg-dark-1'
                )}
                id={name}
                name={name}
                disabled={disabled}
                {...inputProps}
            />
            {showErrorMessage && (
                <ErrorMessage
                    name={name}
                    render={(errorMessage: string) => (
                        <div
                            className={cn(
                                "tx-large-semibold absolute left-0 top-[72px] z-10 flex min-h-[33px] w-full items-center rounded-[15px] border border-gray-4 bg-red-1 px-[15px] pb-[6px] pt-[9px] text-state-error shadow-lightCard before:absolute before:-top-[5px] before:left-[30px] before:h-2 before:w-2 before:-rotate-45 before:border-b-0 before:border-l-0 before:border-r before:border-t before:border-r-gray-4 before:border-t-gray-4 before:bg-red-1 before:content-[''] group-[.promo-code-widget]:top-20 group-[.promo-code-widget]:h-max group-[.promo-code-widget]:w-[252px] dark:border-blue-1 dark:bg-red-2 dark:shadow-dark before:dark:border-blue-1 before:dark:bg-red-2 md:top-[76px] md:group-[.promo-code-widget]:top-[98px] md:group-[.promo-code-widget]:w-[505px] lg:top-[89px] lg:h-10 lg:group-[.promo-code-widget]:w-[338px]",
                                errorClassName
                            )}
                        >
                            <div className="flex w-full items-start gap-x-[6px] md:gap-x-[7px]">
                                <div className="flex-shrink-0">{errorIcon}</div>

                                <span>{errorMessage}</span>
                            </div>
                        </div>
                    )}
                />
            )}
        </div>
    );
});
